import {
  Fragment,
  useEffect,
  useMemo,
  useRef,
  useState,
  type PropsWithChildren,
} from "react";

import clsx from "clsx";
import { isEqual } from "lodash";
import moment from "moment";
import { useToasts } from "react-toast-notifications";
import { useOnClickOutside } from "usehooks-ts";

import { GenericTooltip, Tooltip } from "@components/tooltip-container";
import {
  HistoricalPeriodChoice,
  historicalPeriodChoiceToPretty,
  useMMMDateRange,
  useMMMPeriodSelector,
} from "@features/mmm/hooks/mmm-url-state";
import { useHiddenBudgetMixes } from "@features/mmm/hooks/use-hidden-budget-mixes";
import { formatDate, getDate } from "@features/mmm/utils/formatters";
import {
  Popover,
  PopoverButton,
  PopoverPanel,
  Transition,
} from "@headlessui/react";
import { usePrevious } from "@hooks/use-previous";
import { type FullBudgetMixResponse } from "@north-beam/nb-common";
import { ExploreBudgetsTrackedComponent } from "@pages/mmm/amplitude-tracking/components";
import { MMMTab } from "@pages/mmm/amplitude-tracking/mmm-event";
import { useTrackMMMEvent } from "@pages/mmm/amplitude-tracking/use-track-mmm-event";
import { useClientSettings } from "@pages/mmm/hooks/use-client-settings";
import { useClientSettingsColorMap } from "@pages/mmm/hooks/use-client-settings-color-map";
import { useSelectedCycle } from "@pages/mmm/hooks/use-selected-cycle";
import { TertiaryButton } from "@shared/buttons";
import { DateRangeCalendar } from "@shared/date-range-calendar";
import { renderChevronAnimation } from "@shared/selects/utils";

import { type HistoricalBudgetMixResponse } from "../../budget-mix-card-container/card-container";
import { BUDGET_MIX_WIDTH_WITH_CHART } from "../../budget-mix-chart/constants";
import { useSelectedCyclePriorPeriod } from "../../hooks/use-selected-cycle-prior-period";
import { useShowReferencePeriod } from "../../hooks/use-show-reference-period";
import { useReferencePerformance } from "../use-reference-performance";

import { ExportBudgetMix } from "./export-budget-mix/export-budget-mix";
import { ExportHistoricalBudgetMix } from "./export-budget-mix/export-historical-budget";
import { SelectableChoice } from "./utils/selectable-choice";

const SET_HISTORICAL_BUDGET_MIX_CARD_PERIOD =
  "Set historical budget mix card period";
const getSetHistoricalBudgetMixAmplitudeData = (periodSelection: string) => ({
  selection: periodSelection,
});

export function HistoricalMainTitleSection({
  historicalData,
}: {
  historicalData: HistoricalBudgetMixResponse;
}) {
  const [dateRange] = useMMMDateRange();
  const { startDate, endDate } = dateRange ?? {};
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);
  const toggleCalendar = () => setIsCalendarOpen(!isCalendarOpen);

  const { referencePeriodClientSetting } = useReferencePerformance();

  const [periodChoice] = useMMMPeriodSelector();
  const clientSettingsColorMap = useClientSettingsColorMap();
  const clientSettingColor =
    clientSettingsColorMap[referencePeriodClientSetting?.id || ""];

  return (
    <MainTitleSectionContainer>
      <div className="text-lg font-bold mt-1">
        {referencePeriodClientSetting ? (
          <div className="flex items-center gap-1">
            <div
              className="h-3.5 w-3.5 rounded"
              style={{
                backgroundColor: clientSettingColor,
              }}
            ></div>
            <div className="font-semibold truncate">
              {referencePeriodClientSetting.name} (Reference period)
            </div>
          </div>
        ) : (
          historicalPeriodChoiceToPretty[periodChoice]
        )}
      </div>
      <div className="flex items-center justify-between w-full">
        <div
          className="flex items-center text-xs cursor-pointer"
          onClick={toggleCalendar}
        >
          <i className="fa-regular fa-calendar text-cosmic mr-1" />
          <span className="font-semibold whitespace-nowrap underline text-cosmic">
            {`${formatDate(startDate)} - ${formatDate(endDate)}`}
          </span>
        </div>
        <HistoricalMixDatePicker
          isCalendarOpen={isCalendarOpen}
          setIsCalendarOpen={setIsCalendarOpen}
        />
        <ExportHistoricalBudgetMix historicalData={historicalData} />
      </div>
    </MainTitleSectionContainer>
  );
}

export function RecommendedMainTitleSection({
  title,
  budgetMix,
  onClickDelete,
  openBudgetMixForecastModal,
  clientSettingColor,
}: {
  budgetMix: FullBudgetMixResponse;
  title: string;
  onClickDelete: () => void;
  openBudgetMixForecastModal: () => void;
  clientSettingColor: string | undefined;
}) {
  return (
    <MainTitleSectionContainer>
      <div
        style={{ maxWidth: `${BUDGET_MIX_WIDTH_WITH_CHART - 10}px` }}
        className="overflow-hidden flex-grow text-lg font-bold truncate whitespace-nowrap mt-1"
      >
        <GenericTooltip noInfoCircle content={title} placement="top">
          {clientSettingColor ? (
            <div
              className="h-3 w-3 rounded-sm inline-block mr-1.5"
              style={{ backgroundColor: clientSettingColor }}
            ></div>
          ) : (
            <i className="fa-sharp fa-solid fa-file-spreadsheet text-gray h-3.5 w-3.5 inline-block mr-1.5" />
          )}
          {title}
        </GenericTooltip>
      </div>
      <div className="flex items-center justify-between w-full">
        <div
          className="flex items-center space-x-1 cursor-pointer"
          onClick={openBudgetMixForecastModal}
        >
          <i className="fa-regular fa-chart-line-up text-cosmic text-xs" />
          <div className="text-cosmic underline text-xs font-semibold cursor-pointer">
            VIEW FORECAST PACING
          </div>
        </div>
        <div className="flex items-center">
          <ExportBudgetMix budgetMix={budgetMix} />
          <GenericTooltip
            content={
              budgetMix.is_adopted
                ? "Adopted budget can't be deleted"
                : "Delete this budget"
            }
            noInfoCircle
          >
            <TertiaryButton
              disabled={budgetMix.is_adopted}
              className={clsx(
                "m-0 p-0 rounded-md h-6 w-6 flex-center text-xs",
                { "hover:bg-[#FDDFDB]": !budgetMix.is_adopted },
              )}
              onClick={() => onClickDelete()}
            >
              <i
                className={clsx("fa-regular fa-trash", {
                  "text-mars": !budgetMix.is_adopted,
                })}
              ></i>
            </TertiaryButton>
          </GenericTooltip>
        </div>
      </div>
    </MainTitleSectionContainer>
  );
}

function HistoricalMixDatePicker({
  isCalendarOpen,
  setIsCalendarOpen,
}: {
  isCalendarOpen: boolean;
  setIsCalendarOpen: (isCalendarOpen: boolean) => void;
}) {
  const trackMMMEvent = useTrackMMMEvent(MMMTab.EXPLORE_BUDGETS);
  const { selectedCycle } = useSelectedCycle();
  const ref = useRef(null);
  const [{ startDate, endDate }, setDateRange] = useMMMDateRange();

  const onChooseDateRange = (startDate: string, endDate: string) => {
    trackMMMEvent({
      component: ExploreBudgetsTrackedComponent.HISTORICAL_BUDGET_MIX_CARD,
      action: "onChange",
      actionInfo: "Change historical budget mix card date range (any)",
    });

    setDateRange({ startDate, endDate });
  };

  const enableMmmReferencePeriod = useShowReferencePeriod();

  const clientSettingsColorMap = useClientSettingsColorMap();
  const {
    clientSettingsWithReferencePeriod,
    setClientSettingWithReferencePeriod,
  } = useReferencePerformance();

  useOnClickOutside(ref, () => setIsCalendarOpen(false));

  const { getSelectedCyclePriorPeriod } = useSelectedCyclePriorPeriod();

  const previousPeriodDateRange = useMemo(
    () => getSelectedCyclePriorPeriod(),
    [getSelectedCyclePriorPeriod],
  );

  const samePeriodLastYearDateRange = useMemo(() => {
    const currentCycleStart = getDate(selectedCycle.date_range.start_date);
    const currentCycleEnd = getDate(selectedCycle.date_range.end_date);

    const formattedStartDate = currentCycleStart
      .subtract(1, "years")
      .format("YYYY-MM-DD");
    const formattedEndDate = currentCycleEnd
      .subtract(1, "years")
      .format("YYYY-MM-DD");
    return {
      startDate: formattedStartDate,
      endDate: formattedEndDate,
    };
  }, [selectedCycle.date_range.start_date, selectedCycle.date_range.end_date]);

  const [periodChoice, setPeriodChoice] = useMMMPeriodSelector();

  useEffect(() => {
    // Without the feature flag enabled, the default period choice is Previous Period
    if (!enableMmmReferencePeriod && ![0, 1, 2].includes(periodChoice)) {
      setPeriodChoice(HistoricalPeriodChoice.PREVIOUS_PERIOD);
    }
  }, [enableMmmReferencePeriod, periodChoice, setPeriodChoice]);

  return (
    <Popover className="nb-input-container relative">
      <PopoverButton
        className="hover:bg-cosmic-4 rounded-md w-6 h-6 text-xs"
        onClick={() => setIsCalendarOpen(!isCalendarOpen)}
      >
        {renderChevronAnimation(isCalendarOpen, false, null, "text-cosmic")}
      </PopoverButton>
      {isCalendarOpen && (
        <Transition
          show={isCalendarOpen}
          as={Fragment}
          enter="transition ease-out duration-200"
          enterFrom="opacity-0 translate-y-1"
          enterTo="opacity-100 translate-y-0"
          leave="transition ease-in duration-150"
          leaveFrom="opacity-100 translate-y-0"
          leaveTo="opacity-0 translate-y-1"
        >
          <PopoverPanel
            static
            ref={ref}
            className={clsx(
              "absolute z-100 mt-0 elevation-1 left-0",
              "rounded-md py-1 bg-v2-primary flex flex-col space-y-2 w-fit",
            )}
          >
            <div className="flex justify-start items-start w-full">
              <div
                className={clsx(
                  "flex flex-col justify-start items-start border-r border-b border-v2-secondary h-[313px]",
                  {
                    "min-w-[144px]": !enableMmmReferencePeriod,
                    "min-w-[230px]": enableMmmReferencePeriod,
                  },
                )}
              >
                <span className="text-gray+1 text-xxs p-2">
                  Select a comparison period
                </span>
                <div className="flex flex-col justify-start items-start w-full m-0 overflow-y-auto max-h-[318px]">
                  <SelectableChoice
                    selectedValue={periodChoice}
                    value={HistoricalPeriodChoice.PREVIOUS_PERIOD}
                    onClick={() => {
                      trackMMMEvent({
                        component:
                          ExploreBudgetsTrackedComponent.HISTORICAL_BUDGET_MIX_DATE_PERIOD_SELECTOR,
                        action: "onSelect",
                        actionInfo: SET_HISTORICAL_BUDGET_MIX_CARD_PERIOD,
                        data: getSetHistoricalBudgetMixAmplitudeData(
                          "Previous period",
                        ),
                      });
                      setPeriodChoice(HistoricalPeriodChoice.PREVIOUS_PERIOD);
                      setDateRange({
                        ...previousPeriodDateRange,
                      });
                      setClientSettingWithReferencePeriod(null);
                      setIsCalendarOpen(false);
                    }}
                  >
                    Previous period
                  </SelectableChoice>
                  <SelectableChoice
                    selectedValue={periodChoice}
                    value={HistoricalPeriodChoice.SAME_PERIOD_LAST_YEAR}
                    onClick={() => {
                      trackMMMEvent({
                        component:
                          ExploreBudgetsTrackedComponent.HISTORICAL_BUDGET_MIX_DATE_PERIOD_SELECTOR,
                        action: "onSelect",
                        actionInfo: SET_HISTORICAL_BUDGET_MIX_CARD_PERIOD,
                        data: getSetHistoricalBudgetMixAmplitudeData(
                          "Same period last year",
                        ),
                      });
                      setPeriodChoice(
                        HistoricalPeriodChoice.SAME_PERIOD_LAST_YEAR,
                      );
                      setDateRange({
                        ...samePeriodLastYearDateRange,
                      });
                      setClientSettingWithReferencePeriod(null);
                      setIsCalendarOpen(false);
                    }}
                  >
                    Same period last year
                  </SelectableChoice>
                  {enableMmmReferencePeriod ? (
                    <>
                      {clientSettingsWithReferencePeriod.length > 0 && (
                        <span className="text-gray+1 text-xxs p-2 font-bold border-t border-v2-secondary w-full">
                          Reference periods
                        </span>
                      )}
                      {clientSettingsWithReferencePeriod.map(
                        (clientSetting) => {
                          const clientSettingColor =
                            clientSettingsColorMap[clientSetting?.id || ""];

                          /* eslint-disable @typescript-eslint/no-non-null-assertion */
                          return (
                            <SelectableChoice
                              key={clientSetting.id}
                              selectedValue={periodChoice}
                              value={
                                clientSetting.id as unknown as HistoricalPeriodChoice
                              }
                              onClick={() => {
                                setClientSettingWithReferencePeriod(
                                  clientSetting,
                                );
                                setIsCalendarOpen(false);
                              }}
                            >
                              <Tooltip
                                placement="right"
                                content={
                                  <div className="space-y-2.5">
                                    <div className="flex items-center mb-1 gap-1">
                                      <div
                                        className="h-3 w-3 rounded"
                                        style={{
                                          backgroundColor: clientSettingColor,
                                        }}
                                      ></div>
                                      <div className="text-xs font-semibold">
                                        {clientSetting.name}
                                      </div>
                                    </div>
                                    <div className="text-2xs">
                                      Reference period
                                    </div>
                                    <div className="text-base">
                                      {formatDate(
                                        clientSetting.reference_start_date!,
                                      )}{" "}
                                      -{" "}
                                      {formatDate(
                                        clientSetting.reference_end_date!,
                                      )}
                                    </div>
                                  </div>
                                }
                                noInfoCircle
                              >
                                <div className="flex items-center justify-between">
                                  <div className="flex items-center gap-1">
                                    <div
                                      className="h-3 w-3 rounded"
                                      style={{
                                        backgroundColor: clientSettingColor,
                                      }}
                                    ></div>
                                    <span className="text-xs truncate">
                                      {clientSetting.name}
                                    </span>
                                  </div>
                                </div>
                              </Tooltip>
                            </SelectableChoice>
                          );
                          /* eslint-enable @typescript-eslint/no-non-null-assertion */
                        },
                      )}
                    </>
                  ) : (
                    <SelectableChoice
                      selectedValue={periodChoice}
                      value={HistoricalPeriodChoice.CUSTOM}
                      onClick={(val) => {
                        trackMMMEvent({
                          component:
                            ExploreBudgetsTrackedComponent.HISTORICAL_BUDGET_MIX_DATE_PERIOD_SELECTOR,
                          action: "onSelect",
                          actionInfo: SET_HISTORICAL_BUDGET_MIX_CARD_PERIOD,
                          data: getSetHistoricalBudgetMixAmplitudeData(
                            "Custom",
                          ),
                        });

                        setPeriodChoice(val);
                      }}
                    >
                      <div className="flex justify-start items-center gap-2">
                        <i className="fa-regular fa-calendar text-cosmic text-xs"></i>
                        <span className="text-xs">Custom</span>
                      </div>
                    </SelectableChoice>
                  )}
                </div>
              </div>
              <DateRangeCalendar
                maxAllowedDate={moment().subtract(1, "day")}
                value={{ startDate, endDate }}
                onApply={({ startDate: newStartDate, endDate: newEndDate }) => {
                  trackMMMEvent({
                    component:
                      ExploreBudgetsTrackedComponent.HISTORICAL_BUDGET_MIX_CUSTOM_DATE_CALENDAR,
                    action: "onClick",
                    actionInfo: "Set custom historical budget card mix date",
                    data: getSetHistoricalBudgetMixAmplitudeData(
                      `${startDate} - ${endDate}`,
                    ),
                  });

                  onChooseDateRange(newStartDate, newEndDate);

                  setPeriodChoice(HistoricalPeriodChoice.CUSTOM);
                  setClientSettingWithReferencePeriod(null);
                  setIsCalendarOpen(false);
                }}
              />
            </div>
          </PopoverPanel>
        </Transition>
      )}
    </Popover>
  );
}

export function MainTitleSectionContainer({
  children,
}: PropsWithChildren<object>) {
  return <div className="p-2 w-full">{children}</div>;
}
